import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, CheckCircle2, FileText } from "lucide-react";
import { PdfPreview } from "@/components/PdfPreview";

export default function Approval() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bid, setBid] = useState(null);
  const [resolved, setResolved] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/bids/${id}`).then((r) => {
      setBid(r.data);
      const init = {};
      (r.data.form_fields || []).forEach((f) => { if (f.needs_human) init[f.id] = f.value || ""; });
      setResolved(init);
    });
  }, [id]);

  if (!bid) return <div className="p-8 text-sm text-zinc-400 font-mono">Loading…</div>;

  const flagged = (bid.form_fields || []).filter((f) => f.needs_human);
  const clean = (bid.form_fields || []).filter((f) => !f.needs_human && f.source !== "signature");
  const allResolved = flagged.every((f) => (resolved[f.id] || "").trim() !== "");

  const approve = async () => {
    if (!allResolved) { toast.error("Resolve every highlighted field first."); return; }
    setBusy(true);
    try {
      await api.post(`/bids/${id}/approve`, { resolved_fields: resolved, decision: "approve", note });
      toast.success("Approved — finalizing: e-sign, HubSpot, email, ledger.");
      navigate(`/bids/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed.");
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/bids/${id}/approve`, { resolved_fields: {}, decision: "reject", note });
      toast("Bid rejected.");
      navigate(`/bids/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
      setBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-7xl">
      <button onClick={() => navigate(`/bids/${id}`)} data-testid="back-to-run"
        className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mb-4 transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to run
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight text-zinc-900">Human Approval Gate</h1>
          <p className="text-sm text-zinc-500 mt-1">{bid.solicitation_number} · {bid.title}</p>
        </div>
        {bid.amount && <div className="font-display font-bold text-2xl tnum text-zinc-900">{bid.amount}</div>}
      </div>

      <div className="grid grid-cols-12 gap-6 mt-6">
        {/* Left: the real Ironclad-filled SF-1442 + mapped values */}
        <div className="col-span-12 lg:col-span-6 space-y-4">
          <PdfPreview bidId={id} kind="filled" height={560} label="SF-1442 — Ironclad filled (black ink, on-the-line)" />
          <div className="bg-white border border-zinc-200 rounded-sm">
            <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2 text-[10px] tracking-[0.1em] font-semibold text-zinc-500">
              <FileText className="h-3.5 w-3.5" /> Auto-Filled from Company File
            </div>
            <table className="w-full text-sm">
              <tbody>
                {clean.map((f) => (
                  <tr key={f.id} className="border-b border-zinc-100 last:border-0">
                    <td className="px-4 py-2 text-zinc-500 w-1/2">{f.label}</td>
                    <td className="px-4 py-2 font-mono text-xs text-zinc-900">{f.value || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 text-[11px] text-zinc-400 border-t border-zinc-100 flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {clean.length} fields auto-mapped with high confidence.
            </div>
          </div>
        </div>

        {/* Right: flagged fields to resolve */}
        <div className="col-span-12 lg:col-span-6">
          <div className="bg-white border border-amber-200 rounded-sm">
            <div className="px-5 py-3 border-b border-amber-200 bg-amber-50 flex items-center gap-2 text-[10px] tracking-[0.1em] font-semibold text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5" /> Resolve {flagged.length} Uncertain Field(s)
            </div>
            <div className="p-5 space-y-4" data-testid="flagged-fields">
              {flagged.length === 0 && <p className="text-sm text-zinc-500">Nothing flagged — all fields mapped confidently.</p>}
              {flagged.map((f) => (
                <div key={f.id}>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-zinc-800">{f.label}</label>
                    <StatusBadge status={f.status} />
                  </div>
                  <Input data-testid={`resolve-field-${f.id}`} value={resolved[f.id] || ""}
                    onChange={(e) => setResolved({ ...resolved, [f.id]: e.target.value })}
                    placeholder={f.status === "unmapped" ? "Needs your input" : "Confirm or edit"}
                    className="rounded-sm font-mono" />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <label className="text-sm font-semibold text-zinc-700">Approval note</label>
            <Textarea data-testid="approval-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note recorded in the audit trail." className="mt-2 rounded-sm" />
          </div>

          <div className="mt-4 flex gap-3">
            <Button data-testid="approve-bid-button" onClick={approve} disabled={busy || !allResolved}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-11">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Approve & Finalize
            </Button>
            <Button data-testid="reject-bid-button" onClick={reject} disabled={busy} variant="outline"
              className="rounded-sm h-11 border-red-200 text-red-700 hover:bg-red-50">
              Reject
            </Button>
          </div>
          <p className="text-[11px] text-zinc-400 mt-2 font-mono">
            On approval: e-sign (black ink) → HubSpot sync → email staged for operator → logged to ledger. Federal bids are never auto-sent.
          </p>
        </div>
      </div>
    </div>
  );
}
